import type { Href } from 'expo-router';
import { Redirect, useLocalSearchParams } from 'expo-router';
import {
  TransferPreselectionRouteParams,
  TransferPreselectionRouteValue,
} from 'features/feeBasedInstantTransfer/helpers/transferPreselection';
import type { PropsWithChildren } from 'react';
import { useState } from 'react';
import { FeatureFlagKey } from 'services/statsig/types';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';

const CONTROL_TRANSFER_ROUTE = '/move-money/transfer/transfer-money';
const CONTROL_TRANSFER_PARAMS = [
  'amount',
  'fromId',
  'fromType',
  'source',
  'toId',
  'toType',
  'typeOfAccount',
] as const;

function getSingleRouteValue(
  value: TransferPreselectionRouteValue | undefined,
) {
  return typeof value === 'string' ? value : undefined;
}

export function createControlTransferHref(
  routeParams: TransferPreselectionRouteParams,
): Href {
  const query = CONTROL_TRANSFER_PARAMS.flatMap((param) => {
    const value = getSingleRouteValue(routeParams[param]);
    if (value === undefined) {
      return [];
    }

    const normalizedValue =
      param === 'typeOfAccount' ? value.toUpperCase() : value;
    return `${param}=${encodeURIComponent(normalizedValue)}`;
  }).join('&');

  return (
    query ? `${CONTROL_TRANSFER_ROUTE}?${query}` : CONTROL_TRANSFER_ROUTE
  ) as Href;
}

function FeeBasedInstantTransferRouter({ children }: PropsWithChildren) {
  const routeParams = useLocalSearchParams<TransferPreselectionRouteParams>();
  const { isFeatureEnabled } = useStatsig();
  const [isFBITEnabled] = useState(() =>
    isFeatureEnabled(FeatureFlagKey.FBIT_PILOT),
  );

  if (!isFBITEnabled) {
    return <Redirect href={createControlTransferHref(routeParams)} />;
  }

  return children;
}

export default FeeBasedInstantTransferRouter;
